import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PageTitleService {
  private customTitle: string | null = null;

  constructor(
    private titleService: Title,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {
    this.init();
  }

  private init(): void {
    // Update title on route navigation
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.customTitle = null; // Reset custom title on navigation
        this.updateTitle();
      });

    // Update title on language change
    this.translate.onLangChange.subscribe(() => {
      this.updateTitle();
    });

    // Set initial title
    this.updateTitle();
  }

  /**
   * Set a custom title (e.g., for modals)
   * @param title The title to set
   */
  setCustomTitle(title: string): void {
    this.customTitle = title;
    this.titleService.setTitle(title);
  }

  /**
   * Reset to the route-based title
   */
  resetTitle(): void {
    this.customTitle = null;
    this.updateTitle();
  }

  private updateTitle(): void {
    // If there's a custom title, use it instead
    if (this.customTitle) {
      this.titleService.setTitle(this.customTitle);
      return;
    }

    const titleKey = this.getTitleKey(this.route.root);
    if (titleKey) {
      this.translate.get(titleKey).subscribe(title => {
        this.titleService.setTitle(title);
      });
    }
  }

  private getTitleKey(route: ActivatedRoute): string | null {
    // Traverse the route tree to find the title data
    let child = route;
    while (child) {
      if (child.firstChild) {
        child = child.firstChild;
        if (child.snapshot.data['title']) {
          return child.snapshot.data['title'];
        }
      } else {
        // No more children, check current route
        if (child.snapshot.data['title']) {
          return child.snapshot.data['title'];
        }
        return null;
      }
    }
    return null;
  }
}
